import { createContext, useCallback, useContext, useEffect, useId, useLayoutEffect, useMemo, useRef, useState, type PointerEvent as RPointerEvent, type ReactNode } from "react";
import { createPortal } from "react-dom";
import {
  availabilityWord, capitalise, lastDealingsLine, partsOrText, personSummary, placeCard, seemsPhrase, segments, towardYouPhrase,
} from "../people";
import { Portrait } from "../portrait/Portrait";
import type { PersonCard, ProfileView, TextPart } from "../types";

/** Who everyone is and how they seem to you, for hover cards anywhere in the office. */
interface PeopleIndex { people: Map<string, PersonCard>; profiles: Record<string, ProfileView> }

const PeopleContext = createContext<PeopleIndex>({ people: new Map(), profiles: {} });

export function PeopleProvider({ people, profiles, children }: { people: PersonCard[]; profiles: Record<string, ProfileView> | undefined; children: ReactNode }) {
  const value = useMemo(() => ({ people: new Map(people.map((p) => [p.id, p])), profiles: profiles ?? {} }), [people, profiles]);
  return <PeopleContext.Provider value={value}>{children}</PeopleContext.Provider>;
}

export function usePeople(): PeopleIndex {
  return useContext(PeopleContext);
}

const OPEN_DELAY = 200;
const CLOSE_DELAY = 150;

/** Only one card is open at a time. */
let closeCurrent: (() => void) | null = null;

/** The card itself (visual only; screen readers get the same words through aria-describedby). */
export function PersonCardBody({ person, profile }: { person: PersonCard; profile: ProfileView }) {
  const away = availabilityWord(person.availability);
  const seems = seemsPhrase(profile.demeanour.seems);
  const last = lastDealingsLine(profile.lastDealings);
  return (
    <>
      <div className="person-pop__head">
        <Portrait portraitId={person.portraitId} seed={person.id} name={person.name} grade={person.grade} expression="neutral" size={48} decorative />
        <div>
          <p className="person-pop__name">{person.name}</p>
          <p className="person-pop__role">{person.role}</p>
          {away && <p className="person-pop__away">{away}</p>}
        </div>
      </div>
      {profile.bio && <p className="person-pop__bio">{profile.bio}</p>}
      <p className="person-pop__label">How they seem with you</p>
      <p className="person-pop__seem">{capitalise(towardYouPhrase(profile.demeanour.towardYou))}.{seems ? ` ${seems}.` : ""}</p>
      {last && <p className="person-pop__last">{last}</p>}
      <p className="person-pop__note">As you see it — people don't always show how they feel.</p>
    </>
  );
}

/**
 * A person's name that shows who they are on hover, keyboard focus or tap (tooltip pattern: the
 * name is described by the card's words; nothing inside the card takes focus; Esc closes it).
 * `passive` is for names inside another control (an option or a list button): hover only, so the
 * outer control keeps its own click and accessible name.
 */
export function PersonRef({ personId, person: given, children, passive = false }: {
  personId: string; person?: PersonCard | null | undefined; children?: ReactNode; passive?: boolean;
}) {
  const { people, profiles } = usePeople();
  const person = people.get(personId) ?? given ?? null;
  const profile = profiles[personId];
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number; side: "below" | "above" } | null>(null);
  const trigger = useRef<HTMLElement | null>(null);
  const card = useRef<HTMLDivElement | null>(null);
  const timer = useRef<number | undefined>(undefined);
  const pointerFocus = useRef(false);
  const descId = useId();

  const clearTimer = () => { if (timer.current !== undefined) { window.clearTimeout(timer.current); timer.current = undefined; } };
  const close = useCallback(() => { clearTimer(); setOpen(false); setPos(null); }, []);
  const show = useCallback(() => {
    clearTimer();
    if (closeCurrent && closeCurrent !== close) closeCurrent();
    closeCurrent = close;
    setOpen(true);
  }, [close]);
  const later = (fn: () => void, ms: number) => { clearTimer(); timer.current = window.setTimeout(fn, ms); };

  useEffect(() => () => { clearTimer(); if (closeCurrent === close) closeCurrent = null; }, [close]);

  // Esc closes the card before anything else hears it; scrolling or resizing closes it too; tapping elsewhere closes it.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      e.stopPropagation();
      e.preventDefault();
      close();
    };
    const onDown = (e: PointerEvent) => {
      const t = e.target as Node;
      if (trigger.current?.contains(t) || card.current?.contains(t)) return;
      close();
    };
    document.addEventListener("keydown", onKey, true);
    document.addEventListener("pointerdown", onDown, true);
    window.addEventListener("scroll", close, true);
    window.addEventListener("resize", close);
    return () => {
      document.removeEventListener("keydown", onKey, true);
      document.removeEventListener("pointerdown", onDown, true);
      window.removeEventListener("scroll", close, true);
      window.removeEventListener("resize", close);
    };
  }, [open, close]);

  useLayoutEffect(() => {
    if (!open || !trigger.current || !card.current) return;
    const a = trigger.current.getBoundingClientRect();
    const c = card.current.getBoundingClientRect();
    setPos(placeCard(a, { width: c.width, height: c.height }, { width: window.innerWidth, height: window.innerHeight }));
  }, [open]);

  if (!person || !profile) return <>{children ?? person?.name ?? null}</>;
  const label = children ?? person.name;

  const hover = {
    onPointerEnter: (e: RPointerEvent) => { if (e.pointerType === "mouse") later(show, OPEN_DELAY); },
    onPointerLeave: (e: RPointerEvent) => { if (e.pointerType === "mouse") later(close, CLOSE_DELAY); },
  };

  const popup = open && typeof document !== "undefined" ? createPortal(
    <div ref={card} className={`person-pop person-pop--${pos?.side ?? "below"}`} aria-hidden="true"
      style={pos ? { top: pos.top, left: pos.left } : { top: 0, left: 0, visibility: "hidden" }}
      onPointerEnter={() => clearTimer()} onPointerLeave={(e) => { if (e.pointerType === "mouse") later(close, CLOSE_DELAY); }}
      onClick={(e) => e.stopPropagation()} onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); }}>
      <PersonCardBody person={person} profile={profile} />
    </div>,
    document.body,
  ) : null;

  if (passive) {
    return (
      <span ref={(el) => { trigger.current = el; }} className="person-ref person-ref--passive" {...hover}>
        {label}{popup}
      </span>
    );
  }

  return (
    <span className="person-ref">
      <button ref={(el) => { trigger.current = el; }} type="button" className="person-ref__btn" aria-describedby={descId}
        {...hover}
        onPointerDown={() => { pointerFocus.current = true; }}
        onFocus={() => { if (!pointerFocus.current) show(); pointerFocus.current = false; }}
        onBlur={() => { pointerFocus.current = false; close(); }}
        onClick={(e) => {
          // A mouse click keeps the card open; a tap or Enter toggles it.
          const mouse = (e.nativeEvent as PointerEvent).pointerType === "mouse";
          if (mouse) show();
          else if (open) close();
          else show();
        }}>
        {label}
      </button>
      <span id={descId} hidden>{personSummary(person, profile)}</span>
      {popup}
    </span>
  );
}

/** Authored text with the people it names linked to their cards. */
export function Parts({ parts, text, passive = false }: { parts: TextPart[] | undefined | null; text: string; passive?: boolean }) {
  const { profiles } = usePeople();
  const segs = segments(partsOrText(parts, text), (id) => id in profiles);
  return (
    <>
      {segs.map((s, i) => s.kind === "person"
        ? <PersonRef key={i} personId={s.personId} passive={passive}>{s.text}</PersonRef>
        : <span key={i}>{s.text}</span>)}
    </>
  );
}
